/**
 * Locale-aware absolute & relative date/time formatting — the single standard
 * for the player app. Built on date-fns; pass `i18n.language` so output follows
 * the selected locale. Lao/Burmese fall back to enUS for month names (date-fns
 * has no locale for them); duration/unit labels use i18n keys instead — see
 * ./duration.ts. Bangkok-pinned times live in ./bangkok.ts.
 */
import { format, formatDistanceToNow } from 'date-fns';
import { dateFnsLocale } from './locale.js';

function parse(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(iso: string, lang: string): string {
  const d = parse(iso);
  return d ? format(d, 'd MMM yyyy', { locale: dateFnsLocale(lang) }) : iso;
}

export function formatTime(iso: string, lang: string): string {
  const d = parse(iso);
  return d ? format(d, 'HH:mm', { locale: dateFnsLocale(lang) }) : iso;
}

export function formatDateTime(iso: string, lang: string): string {
  const d = parse(iso);
  return d ? format(d, 'd MMM yyyy HH:mm', { locale: dateFnsLocale(lang) }) : iso;
}

/** Relative time like "about 2 hours ago" / "in 3 days". Falls back to the raw string. */
export function formatRelative(iso: string, lang: string): string {
  const d = parse(iso);
  return d ? formatDistanceToNow(d, { addSuffix: true, locale: dateFnsLocale(lang) }) : iso;
}

/**
 * Day boundaries as ISO instants, for sending a picked date range to a server
 * that filters on a timestamp.
 *
 * The history page used to apply its date range in the browser over whatever
 * pages happened to be loaded, so "last month" filtered 20 rows and returned
 * nothing for a period the player certainly played in (WEB-23). Sending the
 * range means the boundaries have to be explicit: a bare `Date` is midnight
 * local, which silently excludes everything the player did on the last day they
 * picked.
 */
export function startOfDayIso(d: Date): string {
  const start = new Date(d);
  start.setHours(0, 0, 0, 0);
  return start.toISOString();
}

export function endOfDayIso(d: Date): string {
  const end = new Date(d);
  end.setHours(23, 59, 59, 999);
  return end.toISOString();
}
